import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { Search, Filter, MoreHorizontal, Plus, Download, Printer, Edit, Trash2 } from 'lucide-react';
import PagamentoModal from '../../components/cobrancas/PagamentoModal';
import { useNotification } from '../../hooks/useNotification';

interface PagamentoAvulso {
  id: string;
  alunoId: string;
  alunoNome: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  dataVencimento: string;
  status: 'pendente' | 'pago' | 'vencido' | 'cancelado';
  dataPagamento?: string;
  observacoes?: string;
}

const Avulsas: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, NotificationComponent } = useNotification();
  const [pagamentos, setPagamentos] = useState<PagamentoAvulso[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [showPagamentoModal, setShowPagamentoModal] = useState(false);
  const [selectedPagamentoId, setSelectedPagamentoId] = useState<string | undefined>();

  const loadPagamentos = React.useCallback(async () => {
    if (!userProfile?.empresaCnpj) return;
    
    try {
      setLoading(true);
      const [pagamentosData, alunos] = await Promise.all([
        firebaseService.getPagamentos(userProfile.empresaCnpj),
        firebaseService.getAlunos(userProfile.empresaCnpj)
      ]);

      const pagamentosWithAlunos = pagamentosData.map(pagamento => {
        const aluno = alunos.find(a => a.id === pagamento.alunoId);
        return {
          id: pagamento.id,
          alunoId: pagamento.alunoId,
          alunoNome: aluno?.nome || 'Cliente não encontrado',
          valor: pagamento.valor,
          descricao: pagamento.externalReference || 'Pagamento avulso',
          formaPagamento: pagamento.tipoPagamento,
          dataVencimento: pagamento.dataVencimento,
          status: pagamento.status as any,
          dataPagamento: pagamento.dataPagamento,
          observacoes: pagamento.observacoes
        };
      });

      setPagamentos(pagamentosWithAlunos);
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.empresaCnpj]);

  const handleExcluirPagamento = async (pagamento: PagamentoAvulso) => {
    showConfirm(
      'Excluir Pagamento',
      `Tem certeza que deseja excluir o pagamento de ${pagamento.alunoNome}?`,
      async () => {
        try {
          await firebaseService.deletePagamento(userProfile?.empresaCnpj || '', pagamento.id);
          showSuccess('Sucesso', 'Pagamento excluído com sucesso!');
          loadPagamentos();
        } catch (error) {
          console.error('Erro ao excluir pagamento:', error);
          showError('Erro', 'Erro ao excluir pagamento. Tente novamente.');
        }
      },
      {
        confirmText: 'Excluir',
        cancelText: 'Cancelar'
      }
    );
  };

  useEffect(() => {
    loadPagamentos();
  }, [loadPagamentos]);

  const filteredPagamentos = pagamentos.filter(pagamento =>
    pagamento.alunoNome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pagamento.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 'vencido':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 'pendente':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      case 'cancelado':
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pago':
        return '✓';
      case 'vencido':
        return '⚠';
      case 'pendente':
        return '⏰';
      default:
        return '●';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const totalValue = filteredPagamentos.reduce((sum, pagamento) => sum + pagamento.valor, 0);
  const paidValue = filteredPagamentos
    .filter(p => p.status === 'pago')
    .reduce((sum, pagamento) => sum + pagamento.valor, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Cobranças</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">Avulsas</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Avulsas</h1>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-500">Total de Cobranças</div>
          <div className="text-2xl font-bold text-gray-900">{filteredPagamentos.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-500">Valor Total</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalValue)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-sm font-medium text-gray-500">Valor Recebido</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(paidValue)}</div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Procurar por nome do cliente"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <button
              onClick={() => setShowBatchActions(!showBatchActions)}
              className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span>Ações em lote</span>
            </button>
            
            {showBatchActions && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="py-1">
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <Download className="h-4 w-4" />
                    <span>Exportar dados</span>
                  </button>
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <Printer className="h-4 w-4" />
                    <span>Imprimir boletos</span>
                  </button>
                  <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <Download className="h-4 w-4" />
                    <span>Baixar boletos</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={() => {
              setSelectedPagamentoId(undefined);
              setShowPagamentoModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar cobrança</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Período de vencimento
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <span className="text-gray-500">até</span>
                <input
                  type="date"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Situação
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Aguardando pagamento</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Pago</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Vencido</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Forma de pagamento
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Pix</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Boleto</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Cartão</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
              Limpar
            </button>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
              Aplicar
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Forma de pagamento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de vencimento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPagamentos.map((pagamento) => (
                <tr key={pagamento.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                        <span className="text-green-600 text-sm font-medium">
                          {pagamento.alunoNome.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">{pagamento.alunoNome}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{formatCurrency(pagamento.valor)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{pagamento.descricao}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{pagamento.formaPagamento}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(pagamento.dataVencimento)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pagamento.status)}`}>
                      <span className="mr-1">{getStatusIcon(pagamento.status)}</span>
                      {pagamento.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-primary-600 hover:text-primary-900">
                        <span className="text-lg">$</span>
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <span className="text-lg">↻</span>
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <span className="text-lg">📄</span>
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedPagamentoId(pagamento.id);
                          setShowPagamentoModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <Printer className="h-4 w-4" />
                      </button>
                      <button className="text-primary-600 hover:text-primary-900">
                        <span className="text-lg">✉</span>
                      </button>
                      <button 
                        onClick={() => handleExcluirPagamento(pagamento)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir pagamento"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredPagamentos.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Nenhuma cobrança avulsa encontrada</div>
          <div className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece criando sua primeira cobrança avulsa'}
          </div>
        </div>
      )}

      {/* Modal de Pagamento */}
      <PagamentoModal
        isOpen={showPagamentoModal}
        onClose={() => setShowPagamentoModal(false)}
        pagamentoId={selectedPagamentoId}
        onSuccess={() => {
          loadPagamentos();
          setShowPagamentoModal(false);
        }}
      />

      <NotificationComponent />
    </div>
  );
};

export default Avulsas;
